import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, Clock, CircleDollarSign, Search, HandCoins, ReceiptText } from 'lucide-react';
import api, { fmtIDR, fmtDate } from '@/lib/api';
import { toast } from 'sonner';
import EmptyState from '@/components/common/EmptyState';
import KPICard from '@/components/common/KPICard';

export default function OutstandingPayments() {
  const [txs, setTxs] = useState([]);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all'); // all | overdue | termin | upcoming
  const [payFor, setPayFor] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/transactions/outstanding');
      setTxs(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal memuat data');
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = [...txs];
    if (filter === 'overdue') list = list.filter((t) => t.is_overdue);
    else if (filter === 'termin') list = list.filter((t) => t.is_termin_pending);
    else if (filter === 'upcoming') list = list.filter((t) => t.due_date && !t.is_overdue);
    if (query) {
      const q = query.toLowerCase();
      list = list.filter((t) =>
        (t.customer_name || '').toLowerCase().includes(q)
        || (t.customer_company || '').toLowerCase().includes(q)
        || (t.invoice_no || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [txs, query, filter]);

  const totals = useMemo(() => {
    const totalOutstanding = txs.reduce((a, t) => a + (t.remaining_amount || 0), 0);
    const overdueTotal = txs.filter((t) => t.is_overdue).reduce((a, t) => a + (t.remaining_amount || 0), 0);
    const terminTotal = txs.filter((t) => t.is_termin_pending).reduce((a, t) => a + (t.remaining_amount || 0), 0);
    return {
      totalOutstanding,
      overdueTotal,
      terminTotal,
      overdueCount: txs.filter((t) => t.is_overdue).length,
      terminCount: txs.filter((t) => t.is_termin_pending).length,
    };
  }, [txs]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard label="Total Tunggakan" value={fmtIDR(totals.totalOutstanding)} icon={CircleDollarSign} tone="warning" sub={`${txs.length} invoice belum lunas`} testId="kpi-total-outstanding" />
        <KPICard label="Telat Bayar" value={fmtIDR(totals.overdueTotal)} icon={AlertTriangle} tone="danger" sub={`${totals.overdueCount} invoice lewat jatuh tempo`} testId="kpi-overdue" />
        <KPICard label="Menunggu Termin" value={fmtIDR(totals.terminTotal)} icon={Clock} tone="info" sub={`${totals.terminCount} proyek termin`} testId="kpi-termin" />
        <KPICard label="Total Invoice" value={txs.length} icon={ReceiptText} sub="tempo aktif" />
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Cari nama customer, perusahaan, invoice..." value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9" data-testid="outstanding-search" />
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="max-w-xs" data-testid="outstanding-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Tunggakan</SelectItem>
                <SelectItem value="overdue">Hanya Telat Bayar</SelectItem>
                <SelectItem value="termin">Hanya Menunggu Termin</SelectItem>
                <SelectItem value="upcoming">Belum Jatuh Tempo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filtered.length === 0 ? (
            <EmptyState icon={CircleDollarSign} title="Tidak ada tunggakan" description="Semua transaksi tempo sudah lunas untuk filter ini." />
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="outstanding-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Nama Customer</TableHead>
                    <TableHead>Perusahaan</TableHead>
                    <TableHead className="text-right">Total Belanja</TableHead>
                    <TableHead className="text-right">Sudah Bayar</TableHead>
                    <TableHead className="text-right">Nilai Tunggakan</TableHead>
                    <TableHead>Jatuh Tempo</TableHead>
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((t) => (
                    <TableRow key={t.id} data-testid={`outstanding-row-${t.id}`}>
                      <TableCell>
                        {t.is_overdue ? (
                          <Badge variant="destructive" className="text-[10px]"><AlertTriangle className="h-3 w-3 mr-1" />Telat</Badge>
                        ) : t.is_termin_pending ? (
                          <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100 text-[10px]"><Clock className="h-3 w-3 mr-1" />Termin</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">Belum Jatuh</Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{t.invoice_no}</TableCell>
                      <TableCell className="font-medium">{t.customer_name || '-'}</TableCell>
                      <TableCell>{t.customer_company || <span className="text-muted-foreground">-</span>}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtIDR(t.total)}</TableCell>
                      <TableCell className="text-right tabular-nums text-emerald-700">{fmtIDR(t.paid_amount || 0)}</TableCell>
                      <TableCell className="text-right tabular-nums text-rose-700 font-semibold">{fmtIDR(t.remaining_amount || 0)}</TableCell>
                      <TableCell className="text-sm">
                        {t.due_date ? (
                          <span className={t.is_overdue ? 'text-rose-700 font-semibold' : ''}>{t.due_date}</span>
                        ) : (
                          <span className="text-muted-foreground italic text-xs">Mengikuti termin</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="outline" onClick={() => setDetail(t)} data-testid={`detail-${t.id}`}>Detail</Button>
                        <Button size="sm" onClick={() => setPayFor(t)} data-testid={`pay-${t.id}`}><HandCoins className="h-3.5 w-3.5 mr-1" />Bayar</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <PayDialog tx={payFor} onClose={() => setPayFor(null)} onSaved={() => { setPayFor(null); load(); }} />
      <DetailDialog tx={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

function PayDialog({ tx, onClose, onSaved }) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (tx) {
      setAmount(tx.remaining_amount || '');
      setMethod('cash'); setReference(''); setNote('');
    }
  }, [tx]);

  const save = async () => {
    if (!tx) return;
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error('Nominal harus > 0');
    if (amt > (tx.remaining_amount || 0) + 0.01) return toast.error('Melebihi sisa tunggakan');
    setSaving(true);
    try {
      await api.post(`/transactions/${tx.id}/pay`, { amount: amt, method, reference: reference || null, note: note || null });
      toast.success('Pembayaran tercatat');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!tx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pay-dialog">
        <DialogHeader>
          <DialogTitle>Catat Pembayaran Tempo</DialogTitle>
        </DialogHeader>
        {tx && (
          <div className="space-y-3">
            <div className="rounded-lg bg-muted/40 p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Invoice</span><span className="font-mono">{tx.invoice_no}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span className="font-medium">{tx.customer_name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="tabular-nums font-semibold">{fmtIDR(tx.total)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Sudah bayar</span><span className="tabular-nums text-emerald-700">{fmtIDR(tx.paid_amount || 0)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Sisa tunggakan</span><span className="tabular-nums text-rose-700 font-semibold">{fmtIDR(tx.remaining_amount || 0)}</span></div>
            </div>
            <div className="space-y-1.5">
              <Label>Nominal Pembayaran <span className="text-rose-600">*</span></Label>
              <Input type="number" min="1" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="pay-amount" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Metode</Label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Tunai</SelectItem>
                    <SelectItem value="transfer">Transfer</SelectItem>
                    <SelectItem value="qris">QRIS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Referensi</Label>
                <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="No. bukti transfer" data-testid="pay-reference" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Catatan</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="mis. Termin 1 lunas" data-testid="pay-note" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={saving} data-testid="pay-save-button">{saving ? 'Menyimpan...' : 'Simpan Pembayaran'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailDialog({ tx, onClose }) {
  return (
    <Dialog open={!!tx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Detail Tunggakan</DialogTitle>
        </DialogHeader>
        {tx && (
          <div className="space-y-3 text-sm max-h-[70vh] overflow-y-auto">
            <div className="rounded-lg border p-3 space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Invoice</span><span className="font-mono">{tx.invoice_no}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Tanggal transaksi</span><span>{fmtDate(tx.created_at)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span className="font-medium">{tx.customer_name}</span></div>
              {tx.customer_company && <div className="flex justify-between"><span className="text-muted-foreground">Perusahaan</span><span>{tx.customer_company}</span></div>}
              {tx.customer_whatsapp && <div className="flex justify-between"><span className="text-muted-foreground">WhatsApp</span><span className="font-mono">{tx.customer_whatsapp}</span></div>}
              <div className="flex justify-between"><span className="text-muted-foreground">Jatuh Tempo</span><span>{tx.due_date || <em className="text-muted-foreground">Mengikuti termin</em>}</span></div>
            </div>
            <div className="rounded-lg border p-3 space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Total belanja</span><span className="tabular-nums font-semibold">{fmtIDR(tx.total)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Sudah bayar (DP + angsuran)</span><span className="tabular-nums text-emerald-700 font-semibold">{fmtIDR(tx.paid_amount || 0)}</span></div>
              <div className="flex justify-between border-t pt-1"><span className="text-muted-foreground">Nilai tunggakan</span><span className="tabular-nums text-rose-700 font-bold">{fmtIDR(tx.remaining_amount || 0)}</span></div>
            </div>
            {tx.tempo_details && (
              <div className="rounded-lg border p-3 space-y-1 bg-amber-50/50">
                <div className="text-xs font-medium text-amber-800 mb-1">Detail Proyek</div>
                <div>Staff PJ: <b>{tx.tempo_details.staff_pj}</b></div>
                <div>Proyek: <b>{tx.tempo_details.project_name}</b></div>
                {tx.tempo_details.location_kabupaten && <div>Lokasi: {[tx.tempo_details.location_kabupaten, tx.tempo_details.location_desa].filter(Boolean).join(', ')}</div>}
                {tx.tempo_details.location_pj && <div>PJ Lokasi: {tx.tempo_details.location_pj}</div>}
              </div>
            )}
            {(tx.payments || []).length > 0 && (
              <div className="rounded-lg border p-3">
                <div className="text-xs font-medium mb-2">Riwayat Angsuran</div>
                <div className="space-y-1">
                  {tx.payments.map((p) => (
                    <div key={p.id} className="text-xs flex justify-between border-b last:border-0 py-1">
                      <div>
                        <div className="tabular-nums font-semibold">{fmtIDR(p.amount)}</div>
                        <div className="text-muted-foreground">{fmtDate(p.created_at)} • {(p.method || '').toUpperCase()} • {p.created_by_name}</div>
                        {p.reference && <div className="text-muted-foreground">Ref: {p.reference}</div>}
                        {p.note && <div className="text-muted-foreground italic">{p.note}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
